///
/// EarthBrowser
///
/// Created by Matt Giger on 10/28/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Expanded-only row above the strip: the history (blue, left, over the
/// track's history end) and forecast (amber, right) depth around now.
/// Each is a stepper whose value opens a pick list of the stops, so the
/// row replaces the old stepper-plus-presets pair in a third of the width.

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cOffsetSteps } from "@/utils/timeControl"
import { cn } from "@/utils/utils"
import { ChevronDownIcon, LockIcon, MinusIcon, PlusIcon } from "lucide-react"
import { useState } from "react"
import type { TimeControlModel } from "./timeControlModel"

function hoursLabel(hours: number): string {
	return hours >= 24 && hours % 24 === 0 ? `${hours / 24}d` : `${hours}h`
}

export function WindowRow({ model }: { model: TimeControlModel }) {
	const { window, actions } = model
	const spanHours = window.historyHours + window.forecastHours
	return (
		<div className="flex items-center justify-between gap-2 px-2 pt-1.5">
			<DepthStepper
				side="history"
				value={window.historyHours}
				stops={cOffsetSteps.filter(h => h >= 1)}
				lockedAbove={window.maxHistoryHours < window.maxHours ? window.maxHistoryHours : null}
				onAdjust={actions.adjustHistory}
				onPick={actions.setHistoryHours}
			/>

			{/* What the window amounts to: its span and how many frames of the
			    primary layer playback will step through */}
			<span className="hidden sm:block text-[10px] text-white/45 tabular-nums whitespace-nowrap">
				{hoursLabel(spanHours)} window
				{window.frameCount > 0 && (
					<span className="text-white/30"> · {window.frameCount} {window.frameCount === 1 ? "frame" : "frames"}</span>
				)}
			</span>

			<DepthStepper
				side="forecast"
				value={window.forecastHours}
				stops={cOffsetSteps}
				lockedAbove={null}
				onAdjust={actions.adjustForecast}
				onPick={actions.setForecastHours}
			/>
		</div>
	)
}

/**
 * Stepper for hours of history or forecast around now. Steps are
 * geometric (1, 2, 3, 6, 12, 24, 48) so the full range is a few clicks
 * away; the value itself opens the list of stops. History stops past the
 * entitled depth show a lock and open the upsell. The value is unsigned
 * (the label and colour say which side of now it is) so "+" always means
 * more of it; the old signed form made "+" shrink the history side. The
 * history buttons are mirrored ("+" outermost, on the left) so each sits
 * on the side of the strip it moves.
 */
function DepthStepper({ side, value, stops, lockedAbove, onAdjust, onPick }: {
	side: "history" | "forecast"
	value: number
	stops: number[]
	/** Stops above this many hours are locked behind the subscription */
	lockedAbove: number | null
	onAdjust: (direction: 1 | -1) => void
	onPick: (hours: number) => void
}) {
	const isHistory = side === "history"
	const [open, setOpen] = useState(false)
	const tone = isHistory
		? { icon: "stroke-blue-400", text: "text-blue-300", active: "bg-blue-500/25 text-blue-200" }
		: { icon: "stroke-amber-400", text: "text-amber-300", active: "bg-amber-500/25 text-amber-200" }
	const buttonClass = "w-6 h-6 rounded-md hover:bg-white/10 transition-colors flex items-center justify-center"
	const noun = isHistory ? "history" : "forecast"

	return (
		<div
			className={cn("flex items-center gap-1 shrink-0", !isHistory && "flex-row-reverse")}
			title={isHistory ? "Hours of history before now" : "Hours of forecast after now"}
		>
			<span className="text-[10px] text-white/50 capitalize">{noun}</span>
			{/* The buttons mirror about now: the outer one on each side grows
			    the window outward (history "+" is on the left, over the edge it
			    pushes left), the inner one pulls it in. DOM order stays −/value/+ */}
			<div className={cn("flex items-center bg-white/10 rounded-lg p-0.5", isHistory && "flex-row-reverse")}>
				<button
					onClick={() => onAdjust(-1)}
					className={buttonClass}
					aria-label={`Less ${noun}`}
				>
					<MinusIcon className={cn("w-3 h-3", tone.icon)} />
				</button>
				<Popover open={open} onOpenChange={setOpen}>
					<PopoverTrigger asChild>
						<button
							className={cn("h-6 min-w-[2.75rem] px-1 flex items-center justify-center gap-0.5 rounded-md font-mono text-xs tabular-nums hover:bg-white/10 transition-colors", tone.text)}
							aria-label={`Choose hours of ${noun}`}
							title={`${hoursLabel(value)} of ${noun} — click to pick`}
						>
							{hoursLabel(value)}
							<ChevronDownIcon className="w-2.5 h-2.5 opacity-60" />
						</button>
					</PopoverTrigger>
					<PopoverContent
						side="top"
						align={isHistory ? "start" : "end"}
						sideOffset={6}
						className="w-auto p-1 rounded-xl bg-black/80 backdrop-blur-xl border-white/20 text-white shadow-2xl"
					>
						<div className="grid grid-cols-4 gap-0.5" role="menu" aria-label={`Hours of ${noun}`}>
							{stops.map(hours => {
								const locked = lockedAbove !== null && hours > lockedAbove
								const current = hours === value
								return (
									<button
										key={hours}
										role="menuitemradio"
										aria-checked={current}
										onClick={() => {
											onPick(hours)
											setOpen(false)
										}}
										className={cn(
											"h-7 min-w-[2.5rem] px-1.5 flex items-center justify-center gap-0.5 rounded-lg font-mono text-xs tabular-nums transition-colors",
											locked
												? "text-white/40 hover:bg-white/10"
												: current
												? tone.active
												: "text-white/80 hover:bg-white/10"
										)}
										title={locked ? `${hoursLabel(hours)} of history is a subscriber feature — click to upgrade` : undefined}
										aria-label={locked ? `Unlock ${hoursLabel(hours)} of ${noun}` : `${hoursLabel(hours)} of ${noun}`}
									>
										{locked && <LockIcon className="w-2.5 h-2.5 text-cyan-300" />}
										{hoursLabel(hours)}
									</button>
								)
							})}
						</div>
					</PopoverContent>
				</Popover>
				<button
					onClick={() => onAdjust(1)}
					className={buttonClass}
					aria-label={`More ${noun}`}
				>
					<PlusIcon className={cn("w-3 h-3", tone.icon)} />
				</button>
			</div>
		</div>
	)
}
